/**
 * @fileoverview Centralized form validation schemas for note editing.
 * Provides client-side validation that aligns with server-side NoteSchema.
 */

import { z } from 'zod';
import { FIBONACCI_STORY_POINTS, Note, NOTE_CONTENT_MAX_LENGTH } from './note.js';
import { PriorityEnum, type Priority } from './types.js';

/**
 * Tag normalization transform.
 * Converts tags to lowercase with hyphens (e.g., "Hello World" → "hello-world").
 *
 * @param tag - Raw tag string to normalize
 * @returns Normalized tag string in lowercase-hyphenated format
 */
export function normalizeTag(tag: string): string {
  return tag
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Array of tags with normalization
 */
export const tagsArraySchema = z
  .array(z.string())
  .transform((tags) => tags.map(normalizeTag).filter(tag => tag.length > 0))
  .default([]);

/**
 * Metadata JSON string validation.
 * Validates that the string is either empty or valid JSON.
 */
export const metadataStringSchema = z
  .string()
  .default('')
  .refine(
    (val) => {
      if (!val.trim()) return true;
      try {
        JSON.parse(val);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Metadata must be valid JSON or empty' }
  );

/**
 * Priority levels for notes matching the PostgreSQL `priority` enum.
 * Ordered from highest to lowest priority for display.
 */
export const PRIORITY_OPTIONS: readonly { value: Priority; label: string }[] = [
  { value: 'Critical', label: 'Critical' },
  { value: 'Very High', label: 'Very High' },
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
  { value: 'Very Low', label: 'Very Low' },
] as const;

/**
 * Cognitive load levels for notes (0-5 scale)
 */
export const COGNITIVE_LOAD_OPTIONS = [
  { value: 1, label: 'Very Low' },
  { value: 2, label: 'Low' },
  { value: 3, label: 'Medium' },
  { value: 4, label: 'High' },
  { value: 5, label: 'Very High' },
] as const;

/**
 * Story points options using Fibonacci sequence (agile estimation)
 */
export const STORY_POINTS_OPTIONS = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 5, label: '5' },
  { value: 8, label: '8' },
] as const;
/**
 * Note form validation schema (client-side, camelCase).
 * Used by React Hook Form for real-time validation.
 *
 * Field naming conventions:
 * - Client (forms): camelCase (kindId, categoryId)
 * - Server (API/DB): snake_case (kind_id, category_id)
 *
 * Use `noteFormToApiData` to transform before API calls.
 */
export const noteFormSchema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .max(255, 'Title must be 255 characters or less')
    .default(''),

  content: z
    .string()
    .max(NOTE_CONTENT_MAX_LENGTH, 'Content is too long')
    .optional()
    .default(''),

  tags: tagsArraySchema,

  kindId: z.string().default(''),
  categoryId: z.string().default(''),
  subcategoryId: z.string().default(''),
  organisationId: z.string().default(''),
  personId: z.string().default(''),
  projectId: z.string().default(''),
  resourceId: z.string().default(''),
  areaId: z.string().default(''),
  parentNoteId: z.string().default(''),
  statusId: z.string().default(''),

  priority: PriorityEnum.default('Medium'),
  cognitiveLoad: z.number().min(0).max(5).default(3),
  progress: z.number().int().min(0).max(100).default(0),
  startDate: z.string().optional().default(''),
  dueDate: z.string().optional().default(''),
  completedAt: z.string().optional().default(''),
  estimatedMinutes: z.number().optional().nullable(),
  storyPoints: z.number().int().refine(
    (val) => val === null || val === undefined || (FIBONACCI_STORY_POINTS as readonly number[]).includes(val),
    { message: `Story points must be one of: ${FIBONACCI_STORY_POINTS.join(', ')} (Fibonacci sequence)` }
  ).optional().nullable(),
  isFlagged: z.boolean().default(false),
  isMilestone: z.boolean().default(false),

  metadata: metadataStringSchema,
});

/**
 * TypeScript type inferred from form schema
 */
export type NoteFormData = z.infer<typeof noteFormSchema>;

/**
 * API data format (snake_case) for note creation/update.
 * Derived from Note type to ensure synchronization with NoteSchema.
 */
export type NoteApiData = Pick<Note,
  | 'title'
  | 'content'
  | 'kind_id'
  | 'category_id'
  | 'subcategory_id'
  | 'organisation_id'
  | 'person_id'
  | 'project_id'
  | 'resource_id'
  | 'area_id'
  | 'parent_note_id'
  | 'status_id'
  | 'priority'
  | 'cognitive_load'
  | 'progress'
  | 'start_date'
  | 'due_date'
  | 'completed_at'
  | 'estimated_minutes'
  | 'story_points'
  | 'is_flagged'
  | 'is_milestone'
  | 'metadata'
>;

/**
 * Input type for noteApiDataToForm function.
 * Represents a note from the API with nullable fields for reading.
 * Derived from Note type to ensure synchronization with NoteSchema.
 */
export type NoteApiInput = Partial<Note>;

/**
 * Transform form data (camelCase) to API format (snake_case).
 *
 * @param formData - Validated form data from React Hook Form
 * @returns API-ready data with snake_case fields
 *
 * @example
 * ```ts
 * const formData = form.getValues();
 * const apiData = noteFormToApiData(formData);
 * await apiClient.createOne('note', apiData);
 * ```
 */
export function noteFormToApiData(formData: NoteFormData): NoteApiData {
  // Parse metadata if present
  let parsedMetadata: string | null = null;
  if (formData.metadata.trim()) {
    try {
      const obj = JSON.parse(formData.metadata);
      parsedMetadata = JSON.stringify(obj);
    } catch {
      // If parsing fails, validation should have caught it
      parsedMetadata = null;
    }
  }

  return {
    title: formData.title.trim(),
    content: formData.content?.trim() || null,
    kind_id: formData.kindId || null,
    category_id: formData.categoryId || null,
    subcategory_id: formData.subcategoryId || null,
    organisation_id: formData.organisationId || null,
    person_id: formData.personId || null,
    project_id: formData.projectId || null,
    resource_id: formData.resourceId || null,
    area_id: formData.areaId || null,
    parent_note_id: formData.parentNoteId || null,
    status_id: formData.statusId || null,
    priority: formData.priority,
    cognitive_load: formData.cognitiveLoad,
    progress: formData.progress,
    start_date: formData.startDate || null,
    due_date: formData.dueDate || null,
    completed_at: formData.completedAt || null,
    estimated_minutes: formData.estimatedMinutes ?? null,
    story_points: formData.storyPoints ?? null,
    is_flagged: formData.isFlagged,
    is_milestone: formData.isMilestone,
    metadata: parsedMetadata,
  };
}

/**
 * Extract YYYY-MM-DD portion from a date string.
 * Handles ISO datetime (2024-01-15T00:00:00.000Z) and plain date (2024-01-15) formats.
 *
 * @param dateString - Date string to extract from (can be null/undefined)
 * @returns YYYY-MM-DD string or empty string for null/undefined/invalid values
 */
function extractDateOnly(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const match = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

/**
 * Transform API data (snake_case) to form format (camelCase).
 *
 * @param apiData - Note data from API with snake_case fields
 * @param tags - Optional tags array (loaded separately)
 * @returns Form-ready data with camelCase fields
 *
 * @example
 * ```ts
 * const note = await apiClient.getOne('note', noteId);
 * const { tags } = await loadNoteTags(noteId);
 * const formData = noteApiDataToForm(note, tags);
 * form.reset(formData);
 * ```
 */
export function noteApiDataToForm(apiData: NoteApiInput, tags: string[] = []): NoteFormData {
  // Format metadata for display
  let metadataStr = '';
  if (apiData.metadata) {
    try {
      metadataStr = typeof apiData.metadata === 'string'
        ? apiData.metadata
        : JSON.stringify(apiData.metadata, null, 2);
    } catch {
      metadataStr = '';
    }
  }

  return {
    title: apiData.title || '',
    content: apiData.content || '',
    tags: tags,
    kindId: apiData.kind_id || '',
    categoryId: apiData.category_id || '',
    subcategoryId: apiData.subcategory_id || '',
    organisationId: apiData.organisation_id || '',
    personId: apiData.person_id || '',
    projectId: apiData.project_id || '',
    resourceId: apiData.resource_id || '',
    areaId: apiData.area_id || '',
    parentNoteId: apiData.parent_note_id || '',
    statusId: apiData.status_id || '',
    priority: apiData.priority ?? 'Medium',
    cognitiveLoad: apiData.cognitive_load ?? 3,
    progress: apiData.progress ?? 0,
    startDate: extractDateOnly(apiData.start_date),
    dueDate: extractDateOnly(apiData.due_date),
    completedAt: apiData.completed_at || '',
    estimatedMinutes: apiData.estimated_minutes ?? undefined,
    storyPoints: apiData.story_points ?? undefined,
    isFlagged: apiData.is_flagged || false,
    isMilestone: apiData.is_milestone || false,
    metadata: metadataStr,
  };
}

